import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OperationsOrders from "@/pages/OperationsOrders";
import PlaceOrder from "@/pages/PlaceOrder";
import Claims from "@/pages/Claims";
import ClaimOrderForm from "@/pages/ClaimOrderForm";
import Users from "@/pages/admin/Users";
import AgentOrders from "@/pages/admin/AgentOrders";
import SKUs from "@/pages/admin/SKUs";
import Reports from "@/pages/admin/Reports";
import Profits from "@/pages/Profits";
import Notices from "@/pages/admin/Notices";
import Settings from "@/pages/admin/Settings";
import Audits from "@/pages/admin/Audits";
import B2BSubmit from "@/pages/b2b/B2BSubmit";
import B2BTrack from "@/pages/b2b/B2BTrack";
import B2BSubmissions from "@/pages/b2b/B2BSubmissions";
import IBOBills from "@/pages/b2b/IBOBills";
import IBORegister from "@/pages/ibo/IBORegister";
import MyTeam from "@/pages/ibo/MyTeam";
import IBOApplications from "@/pages/admin/IBOApplications";
import IBOGenealogy from "@/pages/admin/IBOGenealogy";
import DistributionDashboard from "@/pages/distribution/DistributionDashboard";
import Territories from "@/pages/distribution/Territories";
import Distributors from "@/pages/distribution/Distributors";
import Parties from "@/pages/distribution/Parties";
import Hub from "@/pages/Hub";
import NoticeBlocker from "@/components/NoticeBlocker";
import "@/App.css";

const SALES_ADMIN_ROLES = ["sales", "admin"];
const ADMIN_VIEW_ROLES = ["admin", "subadmin"];
const CLAIMS_VIEW_ROLES = ["admin", "sales", "approval"];
const REPORTS_ROLES = ["admin", "sales", "operations", "subadmin"];
const B2B_MANAGE_ROLES = ["admin", "accountant"];
const IBO_BILLS_ROLES = ["ibo", "sales", "admin"];
const DM_VIEW_ROLES = ["admin", "accountant", "backend_executive", "market_developer", "distributor", "ibo", "sales"];
const DM_ADMIN_ROLES = ["admin", "accountant", "backend_executive", "market_developer", "ibo"];
const DM_DISTRIBUTOR_ADMIN = ["admin", "backend_executive"];

function Protected({ roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Multi-role support: allow if ANY of the user's roles matches the allowed list
  const userRoles = Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role];
  if (roles && !userRoles.some((r) => roles.includes(r))) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function OrdersRouter() {
  const { user } = useAuth();
  return user?.role === "operations" ? <OperationsOrders /> : <Orders />;
}

function HomeRouter() {
  const { user } = useAuth();
  // Accountant has no core dashboard — send them straight to their B2B queue.
  if (user?.role === "accountant") return <Navigate to="/b2b" replace />;
  // New Distribution-only roles land on the DM dashboard.
  if (["backend_executive", "market_developer", "distributor", "ibo"].includes(user?.role)) {
    return <Navigate to="/distribution" replace />;
  }
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <NoticeBlocker />
        <Routes>
          <Route path="/" element={<Hub />} />
          <Route path="/login" element={<Login />} />
          {/* Public B2B routes — no auth required */}
          <Route path="/b2b/new" element={<B2BSubmit />} />
          <Route path="/b2b/track" element={<B2BTrack />} />
          {/* Public IBO registration */}
          <Route path="/ibo/register" element={<IBORegister />} />
          <Route element={<Protected />}>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<HomeRouter />} />
              <Route path="/orders" element={<OrdersRouter />} />
              <Route element={<Protected roles={SALES_ADMIN_ROLES} />}>
                <Route path="/orders/new" element={<PlaceOrder />} />
                <Route path="/claims/new" element={<ClaimOrderForm />} />
              </Route>
              <Route element={<Protected roles={CLAIMS_VIEW_ROLES} />}>
                <Route path="/claims" element={<Claims />} />
              </Route>
              <Route element={<Protected roles={ADMIN_VIEW_ROLES} />}>
                <Route path="/users" element={<Users />} />
                <Route path="/users/:userId/orders" element={<AgentOrders />} />
                <Route path="/skus" element={<SKUs />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
              <Route element={<Protected roles={["admin"]} />}>
                <Route path="/notices" element={<Notices />} />
                <Route path="/audits" element={<Audits />} />
                <Route path="/admin/ibo-applications" element={<IBOApplications />} />
                <Route path="/admin/ibo-genealogy" element={<IBOGenealogy />} />
              </Route>
              <Route element={<Protected roles={IBO_BILLS_ROLES} />}>
                <Route path="/ibo/my-team" element={<MyTeam />} />
              </Route>
              <Route element={<Protected roles={B2B_MANAGE_ROLES} />}>
                <Route path="/b2b" element={<B2BSubmissions />} />
              </Route>
              <Route element={<Protected roles={IBO_BILLS_ROLES} />}>
                <Route path="/b2b/my-bills" element={<IBOBills />} />
              </Route>
              <Route element={<Protected roles={REPORTS_ROLES} />}>
                <Route path="/reports" element={<Reports />} />
              </Route>
              <Route element={<Protected roles={["admin", "operations", "profit_calculator"]} />}>
                <Route path="/profits" element={<Profits />} />
              </Route>
              <Route element={<Protected roles={DM_VIEW_ROLES} />}>
                <Route path="/distribution" element={<DistributionDashboard />} />
                <Route path="/distribution/territories" element={<Territories />} />
                <Route path="/distribution/distributors" element={<Distributors />} />
                <Route path="/distribution/parties" element={<Parties />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
